import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { Layers3, ListChecks, Pencil, Search, Star, Trash2, Eye, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpClient } from '../../../common/httpclient';
import DialogForm from '../../../component/dialog/DialogForm';
import NewItemButton from '../../../component/common/NewItemButton';
import PageScaffold from '../../../component/pageScaffold/PageScaffold';
import DefaultTable from '../../../component/table/DefaultTable';
import usePermissions from '../../../hooks/usePermissions';
import ChecklistViewDialog from './ViewDialog';
import MobileCrudList from '../../../component/common/MobileCrud';
import { KitPill } from '../../../component/common/MobileKit';
import { KitField, KitSelect } from '../../../component/common/MobileKitExtras';
import { protoIcon } from '../../../component/common/ProtoIcon';
import useIsMobileShell from '../../../hooks/useIsMobileShell';

const ChecksIcon = protoIcon('checks');
const LayersIcon = protoIcon('layers');

// Pill "Padrão" (verde + estrela) — DESIGN-SYSTEM.md §2 (pill anatomy),
// mesma convenção de `BooleanPill`/`StatusChip`, aqui com ícone porque o
// protótipo (`prototipo-lista-checklist.html`, `.pill.success.icon-pill`)
// usa um ícone de estrela em vez do ponto padrão.
function DefaultPill() {
    return (
        <Box
            component="span"
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                fontSize: '11px',
                fontWeight: 700,
                lineHeight: 1,
                padding: '4px 10px',
                borderRadius: '20px',
                bgcolor: 'success.bg',
                color: 'success.main',
            }}
        >
            <Star size={11} strokeWidth={0} fill="currentColor" />
            Padrão
        </Box>
    );
}

export default function ChecklistList() {
    const theme = useTheme();
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();

    const isMobileShell = useIsMobileShell();
    const [checklists, setChecklists] = useState([]);
    const [loading, setLoading] = useState(true);
    // Shell mobile: "12 itens · 3 grupos" do cartão (`GET /checklist/{id}/view`, como a lista de grupos).
    const [structure, setStructure] = useState({});
    const [draftDefault, setDraftDefault] = useState('');

    const [defaultFilter, setDefaultFilter] = useState('');
    const [search, setSearch] = useState('');

    const [openDialogConfirmDelete, setOpenDialogConfirmDelete] = useState(false);
    const [checklistToDelete, setChecklistToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const [message, setMessage] = useState('');
    const [severity, setSeverity] = useState('success');
    const [openSnackbar, setOpenSnackbar] = useState(false);

    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [viewChecklistId, setViewChecklistId] = useState(null);

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await httpClient.get('/checklist');
            setChecklists(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Erro ao carregar checklists:', error);
            setChecklists([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (!isMobileShell) return undefined;
        let active = true;
        checklists.filter((c) => !structure[c.id]).forEach(async (tpl) => {
            try {
                const res = await httpClient.get(`/checklist/${tpl.id}/view`);
                const groups = Array.isArray(res.data) ? res.data : [];
                const items = groups.reduce((sum, g) => sum + (Array.isArray(g.group_inspection_items) ? g.group_inspection_items.filter((i) => i.active !== false).length : 0), 0);
                if (active) setStructure((prev) => ({ ...prev, [tpl.id]: { groups: groups.length, items } }));
            } catch (error) {
                console.error(`Erro ao carregar estrutura do template ${tpl.id}:`, error);
            }
        });
        return () => { active = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMobileShell, checklists]);

    const filteredRows = useMemo(() => {
        return checklists.filter((row) => {
            if (defaultFilter === 'sim' && !row.is_default) return false;
            if (defaultFilter === 'nao' && row.is_default) return false;
            if (search) {
                const term = search.toLowerCase();
                const name = row?.name || '';
                const description = row?.description || '';
                if (!name.toLowerCase().includes(term) && !description.toLowerCase().includes(term)) return false;
            }
            return true;
        });
    }, [checklists, defaultFilter, search]);

    const currentDefault = checklists.find((c) => c.is_default);

    const handleClearFilters = () => {
        setDefaultFilter('');
        setSearch('');
    };

    // "Novo template"/"Editar" navegam para o editor de página cheia
    // (`Form.js` redesenhado, FRONTEND-TASKS.md "Templates de checklist —
    // editor") em vez de abrir o drawer simples de nome/descrição/padrão —
    // aquele drawer (`SimpleForm.js`) foi removido: o editor completo agora
    // cobre os mesmos campos de metadado (nome/descrição/padrão) além da
    // montagem dos grupos de inspeção, que o drawer nunca cobriu.
    const handleOpenNew = () => {
        navigate('/vehicle_checklist/new');
    };

    const handleOpenEdit = (row) => {
        navigate(`/checklist/${row.id}/edt`);
    };

    const handleShowSuccessSnackbar = (text) => {
        setSeverity('success');
        setMessage(text || 'Operação realizada com sucesso!');
        setOpenSnackbar(true);
    };

    const handleShowErrorSnackbar = (errorMessage) => {
        const text = typeof errorMessage === 'string'
            ? errorMessage
            : errorMessage != null
                ? JSON.stringify(errorMessage)
                : 'Erro ao realizar operação';
        setSeverity('error');
        setMessage(text);
        setOpenSnackbar(true);
    };

    const handleOpenView = (row) => {
        setViewChecklistId(row.id);
        setViewDialogOpen(true);
    };

    const handleStubGroupsEditor = () => {
        handleShowErrorSnackbar('Editor de grupos de inspeção (arrastar-e-soltar) ainda não foi implementado — item separado do redesign.');
    };

    const handleDeleteChecklist = async () => {
        setDeleting(true);
        try {
            const response = await httpClient.delete(`/checklist/${checklistToDelete}`);
            if (response.status === 204 || response.status === 200) {
                setOpenDialogConfirmDelete(false);
                setChecklistToDelete(null);
                await fetchData();
                handleShowSuccessSnackbar('Template excluído com sucesso!');
            }
        } catch (error) {
            console.error('Erro ao excluir checklist:', error);
            handleShowErrorSnackbar(error.response?.data || error.message || 'Erro ao excluir template');
        } finally {
            setDeleting(false);
        }
    };

    const checklistToDeleteName = checklists.find((c) => c.id === checklistToDelete)?.name || '';

    const columns = [
        {
            name: 'Nome',
            path: 'name',
            render: (row) => (
                <Typography component="span" sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }} title={row.name}>
                    {row.name}
                </Typography>
            ),
        },
        {
            name: 'Descrição',
            path: 'description',
            render: (row) => (
                <Box
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: 320,
                        fontSize: '13px',
                        color: 'text.secondary',
                    }}
                    title={row.description}
                >
                    {row.description || 'Sem descrição'}
                </Box>
            ),
        },
        {
            name: 'Padrão',
            path: 'is_default',
            render: (row) => (
                row.is_default ? <DefaultPill /> : <Typography component="span" sx={{ color: 'text.disabled' }}>—</Typography>
            ),
        },
    ];

    // Ação "Excluir" fica bloqueada via JS no template marcado como padrão —
    // o backend (`DeleteChecklist`) faz apenas soft delete e não impede a
    // exclusão do padrão, então a regra é 100% de front. Ícone fica
    // esmaecido e o clique explica a regra via toast em vez de abrir a
    // confirmação (mesmo tratamento dado, por design, a "grupo em uso" nas
    // próximas listas de checklist).
    const actions = [
        ...(hasPermission('checklist.view') ? [{
            icon: <Eye size={16} strokeWidth={2} />,
            props: {
                onClick: (row) => handleOpenView(row),
                tooltip: 'Visualizar',
            },
        }] : []),
        ...(hasPermission('checklist.update') ? [{
            icon: <Pencil size={16} strokeWidth={2} />,
            props: {
                onClick: (row) => handleOpenEdit(row),
                tooltip: 'Editar',
            },
        }] : []),
        ...(hasPermission('checklist.delete') ? [{
            icon: (row) => <Trash2 size={16} strokeWidth={2} color={row.is_default ? theme.palette.text.disabled : theme.palette.error.main} />,
            props: {
                onClick: (row) => {
                    if (row.is_default) {
                        handleShowErrorSnackbar('Não é possível excluir o template padrão. Defina outro como padrão antes de excluir este.');
                        return;
                    }
                    setChecklistToDelete(row.id);
                    setOpenDialogConfirmDelete(true);
                },
                tooltip: 'Excluir',
            },
        }] : []),
    ];

    // Micro-label fixo do `tb-field` (DESIGN-SYSTEM.md §5.5/§9), mesmo padrão
    // de `pages/categories/List.js`/`pages/services/List.js`.
    const TbLabel = ({ children }) => (
        <Typography sx={{ fontSize: '10.5px', lineHeight: '13px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: '6px' }}>
            {children}
        </Typography>
    );

    const defaultFilterLabel = defaultFilter === 'sim' ? 'Somente padrão' : defaultFilter === 'nao' ? 'Não padrão' : 'Todos';

    if (isMobileShell) {
        const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
        const canView = hasPermission('checklist.view');
        const canUpdate = hasPermission('checklist.update');
        return (
            <PageScaffold links={[]} allowBleed titlePageTarget={'Checklists'} message={message} openSnackbar={openSnackbar} setOpenSnackbar={setOpenSnackbar} severity={severity}>
                <MobileCrudList
                    items={filteredRows}
                    getKey={(row) => row.id}
                    getRow={(row) => {
                        const info = structure[row.id];
                        return {
                            icon: ChecksIcon,
                            tone: row.is_default ? undefined : 'purple',
                            title: row.name,
                            sub: info ? `${plural(info.items, 'item', 'itens')} · ${plural(info.groups, 'grupo', 'grupos')}` : (row.description || undefined),
                            end: row.is_default ? <KitPill tone="success">Padrão</KitPill> : undefined,
                        };
                    }}
                    loading={loading}
                    search={search}
                    onSearchChange={setSearch}
                    searchPlaceholder="Buscar checklist…"
                    filter={{
                        active: Boolean(defaultFilter),
                        onOpen: () => setDraftDefault(defaultFilter),
                        onApply: () => setDefaultFilter(draftDefault),
                        onClear: handleClearFilters,
                        children: (
                            <KitField label="Padrão">
                                <KitSelect value={draftDefault} onChange={(e) => setDraftDefault(e.target.value)} aria-label="Padrão">
                                    <option value="">Todos</option>
                                    <option value="sim">Somente padrão</option>
                                    <option value="nao">Não padrão</option>
                                </KitSelect>
                            </KitField>
                        ),
                    }}
                    emptyIcon={ChecksIcon}
                    emptyTitle="Nenhum checklist encontrado"
                    emptyText="Ajuste a busca ou os filtros para ver outros checklists."
                    fabLabel={hasPermission('checklist.create') ? 'Novo' : undefined}
                    onNew={handleOpenNew}
                    moreItems={hasPermission('checklist.group.view') ? [{ label: 'Grupos de inspeção', icon: LayersIcon, onClick: () => navigate('/checklist/groups') }] : []}
                    onOpen={canView ? (row) => navigate(`/checklist/${row.id}/view`) : (canUpdate ? handleOpenEdit : undefined)}
                />
            </PageScaffold>
        );
    }

    return (
        <PageScaffold links={[]} titlePageTarget={'Templates de Checklist'} message={message} openSnackbar={openSnackbar} setOpenSnackbar={setOpenSnackbar} severity={severity}>
            <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                {/* page-head — ícone-badge + título + descrição + ação "Novo template". */}
                <Box sx={{
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'flex-end' },
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2,
                    mb: 3,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 38,
                            height: 38,
                            borderRadius: '11px',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            background: (t) => t.palette.gradient.brand,
                        }}>
                            <ListChecks size={18} strokeWidth={2} />
                        </Box>
                        <Box>
                            <Typography component="h1" data-page-title sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                                Templates de Checklist
                            </Typography>
                            <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                                Gerencie os modelos de checklist de inspeção usados nas ordens de serviço.
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
                        {hasPermission('checklist.group.view') && (
                            <Button variant="outlined" startIcon={<Layers3 size={16} strokeWidth={2} />} onClick={() => navigate('/checklist/groups')}>
                                Grupos de inspeção
                            </Button>
                        )}
                        {hasPermission('checklist.create') && (
                            <NewItemButton label="Novo template" onClick={handleOpenNew} />
                        )}
                    </Box>
                </Box>

                {/* report-toolbar — Padrão/Buscar. */}
                <Card sx={{ mb: 2 }}>
                    <CardContent sx={{
                        p: 2,
                        '&:last-child': { pb: 2 },
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'flex-end',
                        gap: 2,
                    }}>
                        <Box>
                            <TbLabel>Padrão</TbLabel>
                            <FormControl size="small" sx={{ minWidth: 160 }}>
                                <Select
                                    value={defaultFilter}
                                    displayEmpty
                                    onChange={(e) => setDefaultFilter(e.target.value)}
                                    renderValue={() => defaultFilterLabel}
                                    sx={{ height: 40, fontSize: '13px', borderRadius: '9px' }}
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    <MenuItem value="sim">Somente padrão</MenuItem>
                                    <MenuItem value="nao">Não padrão</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>

                        <Box sx={{ flex: '0 0 260px' }}>
                            <TbLabel>Buscar</TbLabel>
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="Nome ou descrição do template…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search size={15} strokeWidth={2} />
                                        </InputAdornment>
                                    ),
                                    sx: { height: 40, fontSize: '13px', borderRadius: '9px' },
                                }}
                            />
                        </Box>

                        <Box sx={{ flex: '1 1 auto' }} />

                        <Button variant="outlined" size="small" onClick={handleClearFilters} sx={{ height: '33px' }}>
                            Limpar
                        </Button>
                    </CardContent>
                </Card>

                {/* chips-row — filtros ativos removíveis + contagem/"padrão atual". */}
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2, rowGap: 1 }}>
                    {defaultFilter && (
                        <Chip
                            label={`Padrão: ${defaultFilterLabel}`}
                            size="small"
                            onDelete={() => setDefaultFilter('')}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'info.bg', color: 'primary.main' }}
                        />
                    )}
                    {search && (
                        <Chip
                            label={`Busca: "${search}"`}
                            size="small"
                            onDelete={() => setSearch('')}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                        />
                    )}
                    <Chip
                        label={`${filteredRows.length} template${filteredRows.length === 1 ? '' : 's'} encontrado${filteredRows.length === 1 ? '' : 's'}`}
                        size="small"
                        sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                    />
                    <Chip
                        label={`Padrão atual: ${currentDefault ? currentDefault.name : 'nenhum definido'}`}
                        size="small"
                        sx={{ bgcolor: 'success.bg', color: 'success.main' }}
                    />
                </Stack>

                <DefaultTable
                    colunms={columns}
                    data={filteredRows}
                    iconsActions={actions}
                    onRowClick={hasPermission('checklist.view') ? handleOpenView : undefined}
                    loading={loading}
                    rowPerPage={8}
                    rowsPerPageOptions={[5, 8, 15]}
                    itemLabel="templates"
                    sortableFields={['name', 'description']}
                    defaultSort={{ field: 'name', direction: 'asc' }}
                />
            </Box>

            <DialogForm
                iconType={'warning'}
                open={openDialogConfirmDelete}
                title={'Atenção'}
                confirmButtonText={'Sim, Excluir'}
                content={`Tem certeza que deseja excluir o template "${checklistToDeleteName}"?`}
                secondaryContent={'Esta ação não pode ser desfeita. O template será removido permanentemente do sistema.'}
                onSubmit={handleDeleteChecklist}
                onClose={() => {
                    setOpenDialogConfirmDelete(false);
                    setChecklistToDelete(null);
                }}
                loading={deleting}
                loadingText="Removendo..."
            />

            <ChecklistViewDialog
                open={viewDialogOpen}
                onClose={() => {
                    setViewDialogOpen(false);
                    setViewChecklistId(null);
                }}
                checklistId={viewChecklistId}
                canEdit={hasPermission('checklist.update')}
                onRequestEdit={(chk) => {
                    setViewDialogOpen(false);
                    setViewChecklistId(null);
                    if (chk?.id) {
                        handleOpenEdit(chk);
                    }
                }}
                onStubGroupsEditor={handleStubGroupsEditor}
            />
        </PageScaffold>
    );
}
